import EmptyCartImage from '@assets/icons/empty-cart-image.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ScanIcon from '@assets/icons/scan.svg';
import { Action } from '@/components/navigation-header-content';
import { ActivityIndicator } from '@/components/activity-indicator';
import { CatalogCategoryType } from '@/contexts/item-context-types';
import { CategorySelector } from '@/components/partners/category-selector';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { GTINFilterSidePanel } from '@/components/gtin-filters/gtin-filter-side-panel';
import { isPathInCategoryItemGroups } from '@/utils/category';
import { ItemGroupSelector } from '@/components/item-group-selector';
import { ItemSearchHeaderAction } from '@/components/item-search-header-action';
import {
  PageContainer,
  Props as PageContainerProps,
} from '@/components/page-container';
import {
  SEARCH_SORT_ORDERS,
  SearchSortOrder,
  useUserContext,
  useUserSettingsContext,
} from '@/contexts/user-context';
import { SortOrderButton } from './sort-order-button';
import { SplitFilterContainer } from '@/components/split-filter-container';
import { SupplierCatalogList } from './supplier-catalog-list';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useItemGroupBadgeFilters } from '@/hooks/use-item-group-badge-filters';
import { useItemGroupCategoryFilters } from '@/hooks/use-item-group-category-filters';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSearchVariantPacks } from '@/hooks/use-search-variant-packs';
import { useToasts } from '@/contexts/toast-context';
import { View } from 'react-native';

type Props = PageContainerProps & {
  category?: CatalogCategoryType;
  containerActions?: Action[];
  includeGTINFilters?: boolean;
  onScanPress?: () => void;
  searchSource: string;
};

export const ItemSearchHeaderContainer: React.FC<Props> = ({
  category,
  children,
  containerActions = [],
  onScanPress,
  searchSource,
  includeGTINFilters,
  ...pageContainerProps
}) => {
  const { isSmallLayout } = useLayoutContext();
  const theme = useTheme();
  const { isGuestMode } = useUserContext();
  const {
    badgesFilter,
    categories,
    categoryFilter,
    categoryItemGroups,
    categoryItemGroupFilter,
    clearSearch,
    gtinFilters,
    items,
    searchItemsCallback,
    setCategoryFilter,
    setCategoryItemGroupFilter,
    setBadgesFilter,
  } = useSearchVariantPacks({
    category,
    includeGTINFilters,
    searchSource: `${searchSource}.item_search_header`,
  });

  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');

  const {
    categoryItemGroupsInSelector,
    categoryItemGroupsInSelectorWithoutAllFilter,
  } = useItemGroupCategoryFilters({
    categoryUUIDsToFilterFrom: categoryItemGroups.map(
      (itemGroup) => itemGroup.uuid!,
    ),
    filterByCategoryType: !category,
    selectedCategoryUUID: category?.uuid,
  });
  const { badges } = useItemGroupBadgeFilters({
    category,
    categoryItemGroups,
    items: items ?? undefined,
  });

  const filtersAvailable = useMemo(() => {
    return (
      gtinFilters.listFilters.some((f) => f.isAvailable) ||
      gtinFilters.flagFilters.some((f) => f.isAvailable)
    );
  }, [gtinFilters]);

  const { searchSortOrder, setSearchSortOrder } = useUserSettingsContext();
  const { showErrorToast } = useToasts();
  const [styles] = useStyles();

  const [searchText, setSearchText] = useState<string | null>(null);
  const [isSearchPending, setSearchPending] = useState(false);

  const searchItems = useCallback(
    async (query: string, sortOrder?: SearchSortOrder) => {
      try {
        setSearchPending(true);
        await searchItemsCallback(query, sortOrder || searchSortOrder);
        setSearchPending(false);
      } catch (e) {
        showErrorToast();
        setSearchText(null);
        setSearchPending(false);
      }
    },
    [searchItemsCallback, searchSortOrder, showErrorToast],
  );

  const changeSortOrder = useCallback(
    async (sortOrder: SearchSortOrder) => {
      setSearchSortOrder(sortOrder);

      if (searchText) {
        // Refetch search result to reflect the sort change
        await searchItems(searchText, sortOrder);
      }
    },
    [setSearchSortOrder, searchText, searchItems],
  );

  const categoriesAvailable = itemGroupsEnabled
    ? categoryItemGroups.length > 0
    : categories.length > 0;
  const hasCategoriesOrFilters = categoriesAvailable || filtersAvailable;
  const showSideBar = !isSmallLayout && hasCategoriesOrFilters;

  /**
   * When there are no available categories or filters, and the search results contain more than one item,
   * allow animations for the page container to display the search results.
   *
   * Animations were problematic on the catalog page when focusing on the input via keyboard shortcuts,
   * so animations are disabled if there's only a single item in the search results.
   * I believe that when there's only one result, categories and filters aren't shown.
   */
  const animatePageContainer =
    isSmallLayout || (!hasCategoriesOrFilters && items && items.length > 1);

  // if any of the categories in the filter are not in the categories array
  // the search no longer includes that category. So remove the selected category filter
  useEffect(() => {
    if (itemGroupsEnabled) {
      setCategoryItemGroupFilter((prevFilter) => {
        if (prevFilter) {
          const isFilterInCategoryItemGroups = isPathInCategoryItemGroups(
            showSideBar
              ? categoryItemGroupsInSelectorWithoutAllFilter
              : categoryItemGroupsInSelector,
            prevFilter.path ?? '',
          );
          if (!isFilterInCategoryItemGroups) {
            return null;
          }
        }
        return prevFilter;
      });
    } else {
      setCategoryFilter((prevFilter) => {
        if (prevFilter && !categories.includes(prevFilter)) {
          return null;
        }
        return prevFilter;
      });
    }
  }, [
    categories,
    setCategoryFilter,
    setCategoryItemGroupFilter,
    showSideBar,
    itemGroupsEnabled,
    categoryItemGroupsInSelector,
    categoryItemGroupsInSelectorWithoutAllFilter,
  ]);

  // reset badges to an empty set if any of the previous badges are not included in current badges
  // requires ItemGroups to be enabled because badges are only used on ItemGroups
  useEffect(() => {
    if (itemGroupsEnabled) {
      setBadgesFilter((prevFilter) => {
        if (prevFilter) {
          const badgeUUIDs = new Set(badges.map((b) => b.uuid));
          const filteredSet = new Set(
            [...prevFilter].filter((filter) => badgeUUIDs.has(filter)),
          );

          return filteredSet.size === prevFilter.size ? prevFilter : new Set();
        }

        return prevFilter;
      });
    }
  }, [badges, setBadgesFilter, itemGroupsEnabled]);

  const combinedPageContainerProps = useMemo(() => {
    if (isSmallLayout) {
      return pageContainerProps;
    }

    const baseLargeScreenActions = containerActions;

    if (onScanPress) {
      baseLargeScreenActions.push({
        Icon: ScanIcon,
        iconProps: {
          stroke: theme.sk.color.icon.primary,
        },
        onPress: onScanPress,
        testID: 'scan-barcode-icon',
      });
    }

    const baseNavHeaderProps = searchText
      ? {
          animate: animatePageContainer || false,
          backButtonLabel: i18n.t('back_to', {
            name: category?.name || i18n.t('catalog'),
          }),
          containerStyle: styles.navHeaderContainer,
          onBackPress: () => {
            setSearchText(null);
          },
          subheader: (
            <View style={styles.searchFilterRow}>
              {showSideBar ? null : itemGroupsEnabled ? (
                <ItemGroupSelector
                  data={categoryItemGroupsInSelector}
                  onItemGroupPress={setCategoryItemGroupFilter}
                  selectedItemGroup={categoryItemGroupFilter}
                  trackingKey="item_search_header.item_group_selector"
                />
              ) : (
                <CategorySelector
                  categories={categories}
                  onCategoryPress={setCategoryFilter}
                  selectedCategory={categoryFilter}
                />
              )}
              {isGuestMode ? null : (
                <SortOrderButton
                  onChange={changeSortOrder}
                  supportedSortOptions={SEARCH_SORT_ORDERS}
                  value={searchSortOrder}
                />
              )}
            </View>
          ),
          title: `"${searchText}"`,
        }
      : pageContainerProps.navigationHeaderProps || {};

    return {
      ...pageContainerProps,
      navigationHeaderProps: {
        ...baseNavHeaderProps,
        actions: [
          ...baseLargeScreenActions,
          {
            component: (
              <ItemSearchHeaderAction
                containerStyle={
                  baseLargeScreenActions.length ? styles.scannerMargin : null
                }
                onSubmit={async (value: string | null) => {
                  setSearchText(value);
                  if (value) {
                    await searchItems(value, searchSortOrder);
                  } else {
                    clearSearch();
                  }
                }}
              />
            ),
          },
        ],
        responsive: true,
      },
    };
  }, [
    animatePageContainer,
    categories,
    category,
    categoryFilter,
    categoryItemGroupFilter,
    categoryItemGroupsInSelector,
    changeSortOrder,
    clearSearch,
    containerActions,
    isGuestMode,
    isSmallLayout,
    itemGroupsEnabled,
    onScanPress,
    pageContainerProps,
    searchItems,
    searchSortOrder,
    searchText,
    setCategoryFilter,
    setCategoryItemGroupFilter,
    showSideBar,
    styles,
    theme,
  ]);

  return (
    <PageContainer
      {...combinedPageContainerProps}
      animateNavigationHeader
      showNavigationHeader
    >
      {(scrollViewProps, scrollY) => {
        if (searchText) {
          const searchResults = items || [];
          return (
            <View style={styles.searchResults}>
              {isSearchPending ? (
                <View
                  style={[
                    styles.loadingContainer,
                    animatePageContainer
                      ? scrollViewProps.contentContainerStyle
                      : null,
                  ]}
                >
                  <ActivityIndicator testID="loading-indicator" />
                </View>
              ) : (
                <SplitFilterContainer
                  FilterComponent={
                    itemGroupsEnabled ? (
                      <GTINFilterSidePanel
                        badges={badges}
                        badgesFilter={badgesFilter}
                        gtinFilters={gtinFilters}
                        itemGroupCategories={
                          categoryItemGroupsInSelectorWithoutAllFilter
                        }
                        onItemGroupCategoryPress={setCategoryItemGroupFilter}
                        onSelectBadges={setBadgesFilter}
                        selectedItemGroupCategory={categoryItemGroupFilter}
                      />
                    ) : (
                      <GTINFilterSidePanel
                        categories={categories}
                        gtinFilters={gtinFilters}
                        onCategoryPress={setCategoryFilter}
                        selectedCategory={categoryFilter}
                      />
                    )
                  }
                  filtersVisible={showSideBar}
                >
                  {!searchResults.length ? (
                    <EmptyStateMinimal
                      containerStyle={styles.emptyStateContainer}
                      ImageComponent={EmptyCartImage}
                      text={i18n.t('no_search_results')}
                    />
                  ) : (
                    <SupplierCatalogList
                      items={searchResults}
                      renderSource="catalog_search"
                      scrollViewProps={{
                        ...scrollViewProps,
                        contentContainerStyle: animatePageContainer
                          ? scrollViewProps.contentContainerStyle
                          : null,
                      }}
                      searchText={searchText}
                      showSingleOption={false}
                    />
                  )}
                </SplitFilterContainer>
              )}
            </View>
          );
        }

        return typeof children === 'function'
          ? children(scrollViewProps, scrollY)
          : children;
      }}
    </PageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  emptyStateContainer: {
    backgroundColor: theme.colors.transparent,
  },
  loadingContainer: {
    marginTop: 16,
  },
  navHeaderContainer: {
    marginBottom: 16,
  },
  scannerMargin: {
    marginLeft: 12,
  },
  searchFilterRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  searchResults: {
    flex: 1,
  },
}));
